import { Component, Fragment, type ReactNode } from 'react';

/**
 * A part of the shell in its own chunk, contained when the chunk does not load (offline): the
 * failure is drawn where the part would be, by `failed`, and never thrown to the route's error
 * screen. Its retry fetches the chunk afresh (`preload` resets a failed load) and draws the part
 * again, so a failure while still offline is drawn again in the same place.
 *
 * ⚠️ In the launch entry, which never grows (`ENTRY_BYTES_BUDGET`), and it cannot be a chunk of its
 * own: it is drawn exactly when chunks do not load. One boundary serves every such part (the bar's
 * results, the command palette); each part says only its own line.
 */
export class ChunkFailBoundary extends Component<
  { retry: () => Promise<void>; failed: (retry: () => void) => ReactNode; children: ReactNode },
  { failed: boolean; attempt: number }
> {
  state = { failed: false, attempt: 0 };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render(): ReactNode {
    const { failed, attempt } = this.state;
    if (!failed) return <Fragment key={attempt}>{this.props.children}</Fragment>;
    return this.props.failed(
      () =>
        void this.props
          .retry()
          .finally(() => this.setState({ failed: false, attempt: attempt + 1 })),
    );
  }
}
